import { useEffect, useRef, useState } from "react";

/** Easing — easeOutCubic dá o efeito "drop in" suave tipo Apple Health */
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

export default function PostureScoreRing({ score = 0, classification = "" }) {
  const size = 180;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;

  // Score visual animado — começa em 0 e cresce até `score` real ao montar.
  // O score real (props) nunca é alterado — esta é só a representação animada.
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const target = Math.max(0, Math.min(100, Number(score) || 0));

    // Respeita preferência de menos movimento — sem animação, valor final imediato.
    const reduce = typeof window !== "undefined"
      && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setDisplay(target);
      return;
    }

    const duration = 1100; // ms — suave mas não lento
    const startedAt = performance.now();
    const from = 0; // sempre revelar do zero ao mudar de avaliação

    setDisplay(0);

    const tick = (now) => {
      const elapsed = now - startedAt;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(t);
      setDisplay(from + (target - from) * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [score]);

  const pct = Math.max(0, Math.min(100, display)) / 100;
  const offset = c * (1 - pct);

  const color =
    score >= 80 ? "var(--score-good)" :
    score >= 60 ? "var(--score-mid)" : "var(--score-low)";
  const glow =
    score >= 80 ? "rgba(52,211,153,0.6)" :
    score >= 60 ? "rgba(250,204,21,0.55)" : "rgba(244,63,94,0.55)";

  return (
    <div className="flex flex-col items-center" data-testid="posture-score-ring">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          {/* Track */}
          <circle
            cx={size / 2} cy={size / 2} r={r}
            stroke="var(--ring-track)" strokeWidth={stroke} fill="none"
          />
          {/* Progresso animado — stroke-dashoffset sincronizado via state */}
          <circle
            cx={size / 2} cy={size / 2} r={r}
            stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round"
            strokeDasharray={c} strokeDashoffset={offset}
            style={{ filter: `drop-shadow(0 0 10px ${glow})` }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div
            className="font-display text-5xl text-slate-50 tabular-nums"
            data-testid="score-value"
          >
            {Math.round(display)}
          </div>
          <div className="text-xs text-slate-500">/ 100</div>
        </div>
      </div>
      <div className="mt-4 text-sm" style={{ color }}>{classification}</div>
    </div>
  );
}
